/// <reference path="./globals.d.ts" />
/**
 * Token Refresh Service
 *
 * Server-side-bootstrapped OAuth token refresh:
 * - Initialised from the token the .NET backend injects via `window.oktaToken`
 *   (and `window.refreshToken` / `window.tokenExpiresIn`).
 * - Calls a backend endpoint to refresh the access token before it expires.
 * - Used by `useTokenRefresh` (and any consumer that wants reactive token state).
 *
 * This is intentionally provider-agnostic — it just shuttles bearer tokens.
 * The unified `AuthProvider`/`getToken()` flow handles direct Okta and CAD
 * SPA flows; this service is the legacy "backend-injected token" flow.
 */

import type { TokenRefreshServiceInterface } from '../hooks/use-token-refresh';

export interface TokenRefreshResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType: string;
  issuedAt: number;
  scope?: string;
}

export interface TokenState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isRefreshing: boolean;
  lastRefreshTime: number | null;
}

/**
 * Minimal HTTP client surface the service needs. Apps inject their own
 * `apiClient` so we don't pull in a specific HTTP implementation here.
 */
export interface TokenRefreshHttpClient {
  post<T>(url: string, data?: unknown): Promise<T | null>;
}

export interface TokenRefreshOptions {
  /** Endpoint to POST `{ refreshToken }` to. Defaults to `/Auth/RefreshToken`. */
  endpoint?: string;
  /** Optional callback when a refresh fails (e.g., to display a snackbar). */
  onRefreshFailed?: (error: unknown, context: 'manual' | 'scheduled') => void;
}

class TokenRefreshService implements TokenRefreshServiceInterface {
  private tokenState: TokenState = {
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
    isRefreshing: false,
    lastRefreshTime: null,
  };

  private refreshPromise: Promise<TokenRefreshResponse> | null = null;
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private http: TokenRefreshHttpClient | null = null;
  private options: TokenRefreshOptions = {};

  /**
   * Configure the service. Apps call this once during initialization with
   * their api-client and any optional behaviour overrides.
   */
  configure(http: TokenRefreshHttpClient, options: TokenRefreshOptions = {}): void {
    this.http = http;
    this.options = options;
  }

  initializeTokens(accessToken: string, refreshToken?: string, expiresIn?: number): void {
    this.tokenState = {
      accessToken,
      refreshToken: refreshToken || null,
      expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : null,
      isRefreshing: false,
      lastRefreshTime: null,
    };
    this.scheduleRefresh();
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.tokenState.accessToken) return null;
    if (this.isTokenExpired() || this.isTokenNearExpiry()) {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        this.options.onRefreshFailed?.(error, 'manual');
        return null;
      }
    }
    return this.tokenState.accessToken;
  }

  async refreshAccessToken(): Promise<TokenRefreshResponse> {
    if (this.tokenState.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }
    if (!this.http) {
      throw new Error(
        'TokenRefreshService not configured. Call configure(httpClient) during app initialization.',
      );
    }

    this.tokenState.isRefreshing = true;
    try {
      const refreshPromise = this.executeTokenRefresh();
      this.refreshPromise = refreshPromise;
      const response = await refreshPromise;
      this.updateTokenState(response);
      this.scheduleRefresh();
      return response;
    } catch (error) {
      this.options.onRefreshFailed?.(error, 'manual');
      this.clearTokens();
      throw error;
    } finally {
      this.tokenState.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async executeTokenRefresh(): Promise<TokenRefreshResponse> {
    const refreshToken = this.tokenState.refreshToken;
    if (!refreshToken) throw new Error('No refresh token available');
    const endpoint = this.options.endpoint ?? '/Auth/RefreshToken';
    const response = await this.http!.post<TokenRefreshResponse>(endpoint, {
      refreshToken,
    });
    if (!response) throw new Error('Failed to refresh token: empty response');
    return response;
  }

  private updateTokenState(response: TokenRefreshResponse): void {
    this.tokenState = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken || this.tokenState.refreshToken,
      expiresAt: response.issuedAt + response.expiresIn * 1000,
      isRefreshing: false,
      lastRefreshTime: Date.now(),
    };
    // Keep the legacy backend-injected global in sync so any non-React
    // code that reads it sees the refreshed value.
    if (typeof window !== 'undefined' && window.oktaToken !== undefined) {
      window.oktaToken = response.accessToken;
    }
  }

  private scheduleRefresh(): void {
    if (this.refreshTimeoutId) clearTimeout(this.refreshTimeoutId);
    if (!this.tokenState.expiresAt) return;

    const now = Date.now();
    const expiresIn = this.tokenState.expiresAt - now;
    const refreshAt = expiresIn * 0.8;
    const jitter = Math.random() * 5000;
    const scheduleTime = Math.max(0, refreshAt + jitter);

    this.refreshTimeoutId = setTimeout(() => {
      this.refreshAccessToken().catch((error) => {
        this.options.onRefreshFailed?.(error, 'scheduled');
      });
    }, scheduleTime);
  }

  private isTokenExpired(): boolean {
    if (!this.tokenState.expiresAt) return true;
    return Date.now() > this.tokenState.expiresAt;
  }

  private isTokenNearExpiry(): boolean {
    // Defensive guard; getAccessToken() short-circuits via isTokenExpired()
    // when expiresAt is null, so the null branch is unreachable through the
    // public API.
    /* v8 ignore next */
    if (!this.tokenState.expiresAt) return true;
    const fiveMinutesMs = 5 * 60 * 1000;
    return this.tokenState.expiresAt - Date.now() < fiveMinutesMs;
  }

  getTokenState(): TokenState {
    return { ...this.tokenState };
  }

  getTimeUntilExpiry(): number | null {
    if (!this.tokenState.expiresAt) return null;
    return Math.max(0, Math.floor((this.tokenState.expiresAt - Date.now()) / 1000));
  }

  clearTokens(): void {
    this.tokenState = {
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      isRefreshing: false,
      lastRefreshTime: null,
    };
    if (this.refreshTimeoutId) clearTimeout(this.refreshTimeoutId);
    if (typeof window !== 'undefined' && window.oktaToken !== undefined) {
      window.oktaToken = '';
    }
  }

  isRefreshing(): boolean {
    return this.tokenState.isRefreshing;
  }

  isTokenValid(): boolean {
    return this.tokenState.accessToken !== null && !this.isTokenExpired();
  }
}

/** Singleton token refresh service shared across all React apps. */
export const tokenRefreshService = new TokenRefreshService();
